import type { EditorState, Transaction } from "prosemirror-state";
import { schema } from "./schema";
import type { ViewBlockForm } from "../types";

// Form <-> ProseMirror view_block node-attr translation. The dialog edits raw
// strings; node attrs use null for "unset". Kept separate from the dialog view
// so update() can build transactions from the Model's EditorState.

export function defaultViewBlockForm(): ViewBlockForm {
  return {
    viewType: "list",
    filter: "",
    orderBy: "",
    orderDesc: "true",
    groupBy: "",
    columns: "",
    showAllColumns: "true",
    display: "preview-short",
  };
}

export function formFromAttrs(attrs: Record<string, unknown>): ViewBlockForm {
  const s = (k: string, fallback = ""): string => {
    const v = attrs[k];
    return v == null ? fallback : String(v);
  };
  return {
    viewType: s("viewType", "list"),
    filter: s("filter"),
    orderBy: s("orderBy"),
    orderDesc: attrs["orderDesc"] === false ? "false" : "true",
    groupBy: s("groupBy"),
    columns: Array.isArray(attrs["columns"]) ? (attrs["columns"] as string[]).join("\n") : "",
    showAllColumns: attrs["showAllColumns"] === false ? "false" : "true",
    display: s("display", "preview-short"),
  };
}

function normalize(form: ViewBlockForm): Record<string, unknown> {
  const n = (v: string): string | null => v.trim() || null;
  const vt = form.viewType;
  // Network has no ordering; list has no grouping/columns.
  const hasOrder = vt !== "network";
  const hasGroup = vt === "kanban" || vt === "network";
  const isKanban = vt === "kanban";
  // One column value per non-empty line, order preserved.
  const cols = form.columns.split("\n").map((s) => s.trim()).filter((s) => s.length > 0);
  const orderBy = hasOrder ? n(form.orderBy) : null;
  return {
    viewType: vt,
    filter: n(form.filter),
    orderBy,
    // Direction only matters when an order expression is set.
    orderDesc: orderBy ? form.orderDesc !== "false" : null,
    groupBy: hasGroup ? n(form.groupBy) : null,
    // Column display order only applies to kanban.
    columns: isKanban && cols.length ? cols : null,
    showAllColumns: isKanban ? form.showAllColumns !== "false" : null,
    display: form.display,
  };
}

export function insertViewBlockTr(state: EditorState, form: ViewBlockForm): Transaction {
  const node = schema.nodes["view_block"]!.create(normalize(form));
  return state.tr.replaceSelectionWith(node);
}

export function updateViewBlockTr(state: EditorState, pos: number, form: ViewBlockForm): Transaction {
  return state.tr.setNodeMarkup(pos, null, normalize(form));
}
